import type { AdvisorActionType, AdvisorFeedback } from '@ppwr/convex/players/types'
import {
  ADVISOR_CONCEPT_LABELS,
  ADVISOR_CONVERSATION_HEADLINE_COPY,
  ADVISOR_HEADLINE_COPY,
  ADVISOR_UI_COPY,
  pickAdvisorCopy
} from '@ppwr/convex/players/advisor.app.copy'

export const ACTION_ADVISOR_FEEDBACK_GRACE_MS = 30000

export const ACTION_ADVISOR_STATUS = {
  available: 'available',
  userDisabled: 'user_disabled',
  unsupportedMode: 'unsupported_mode',
  featureDisabled: 'feature_disabled',
  missingMode: 'missing_mode',
  missingActor: 'missing_actor'
} as const

// Player-visible advisor UI labels live in the shared copy module so they are
// enumerated in ADVISOR_OUTPUT.md alongside every other advisor string.
export const ACTION_ADVISOR_COPY = ADVISOR_UI_COPY

// The headline pools (per grade and per conversation kind) also live in the
// shared copy module; this file only owns the composition logic.

export function buildActionAdvisorHeadline(feedback: AdvisorFeedback): string {
  const recommendedAction = toCoachPhrase(feedback.recommendedActionLabel)
  const copyOffset = feedback.conversation?.copyOffset ?? 0
  const seed = [
    feedback.grade,
    feedback.playerActionLabel,
    feedback.recommendedActionLabel,
    feedback.primaryReason,
    feedback.differenceReason ?? ''
  ].join(':')
  const args = {
    recommendedAction,
    RecommendedAction: capitalizeFirst(recommendedAction)
  }

  switch (feedback.conversation?.kind) {
    case 'advice':
      return pickAdvisorCopy(seed, getAdviceHeadlinePool(feedback), {}, copyOffset)
    case 'repeated':
      return pickAdvisorCopy(
        seed,
        ADVISOR_CONVERSATION_HEADLINE_COPY.repeated,
        { ...args, Concept: capitalizeFirst(getConceptLabel(feedback)) },
        copyOffset
      )
    case 'corrected':
      return pickAdvisorCopy(seed, ADVISOR_CONVERSATION_HEADLINE_COPY.corrected, args, copyOffset)
    case 'continued':
      return pickAdvisorCopy(seed, ADVISOR_CONVERSATION_HEADLINE_COPY.continued, args, copyOffset)
    case 'reversed':
      return pickAdvisorCopy(
        seed,
        ADVISOR_CONVERSATION_HEADLINE_COPY.reversed,
        { ...args, Street: capitalizeFirst(feedback.decision.street) },
        copyOffset
      )
    default:
      break
  }

  switch (feedback.grade) {
    case 'aligned':
      return pickAdvisorCopy(seed, ADVISOR_HEADLINE_COPY.aligned, args, copyOffset)
    case 'close':
      return pickAdvisorCopy(seed, ADVISOR_HEADLINE_COPY.close, args, copyOffset)
    default:
      return pickAdvisorCopy(seed, ADVISOR_HEADLINE_COPY.offProfile, args, copyOffset)
  }
}

const AGGRESSIVE_ACTION_TYPES = new Set<AdvisorActionType>(['bet', 'raise', 'allin'])

// Turn-start advice is normally tonally neutral, because the supporting line
// may recommend folding. A premium starting hand with an aggressive
// recommendation is the one spot where enthusiasm cannot contradict the
// supporting line, so it gets the "I like this hand" pool. Calls, checks, and
// folds with a premium hand keep the neutral pool.
function getAdviceHeadlinePool(feedback: AdvisorFeedback): readonly string[] {
  const { street, preflopTier, recommendedAction } = feedback.decision
  const isPremiumAggressiveSpot =
    street === 'preflop' && preflopTier === 'premium' && AGGRESSIVE_ACTION_TYPES.has(recommendedAction)
  return isPremiumAggressiveSpot
    ? ADVISOR_CONVERSATION_HEADLINE_COPY.advicePremiumHand
    : ADVISOR_CONVERSATION_HEADLINE_COPY.advice
}

// The coaching-concept label for the {Concept} filler — player-facing words
// ('price', not 'potOdds'). The label table lives in the shared copy bank
// (ADVISOR_CONCEPT_LABELS) so ADVISOR_OUTPUT.md enumerates every variant.
function getConceptLabel(feedback: AdvisorFeedback): string {
  return (
    ADVISOR_CONCEPT_LABELS[feedback.decision.concept as keyof typeof ADVISOR_CONCEPT_LABELS] ??
    feedback.decision.concept
  )
}

function capitalizeFirst(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1)
}

function toCoachPhrase(actionLabel: string): string {
  return actionLabel.charAt(0).toLowerCase() + actionLabel.slice(1)
}
